import { theme } from '@mi-front/ui';
import styled from 'styled-components';
import { TRACK_LABELED_PILL_RADIUS_PX } from '../../../../stream-track-visual-tokens';

type TopicPillProps = { color?: string; isFocused?: boolean };

/**
 * Inset start tick for topic pills. Prefer box-shadow over border-left so the mark
 * stays crisp against the capsule radius and does not eat into content width.
 */
const TOPIC_PILL_START_SHADOW = 'inset 2px 0 0 rgba(0, 0, 0, 0.3)';

/**
 * Pill-shaped bar for a single live topic on the track.
 * Shows display title (refined or canonical), truncated with ellipsis when needed.
 * When color is set (category color), text color is chosen for contrast.
 * Left inset shadow marks the temporal start (helps when overlapping pills stack).
 * Vertical bleed into the next track is clipped by the lane surface (`overflow: hidden`),
 * not here — `overflow: visible` keeps StickyPillLabel working.
 */
export const TopicPill = styled.div<TopicPillProps>`
  font-size: 11px;
  border-radius: ${TRACK_LABELED_PILL_RADIUS_PX}px;
  padding: 4px 8px;
  background-color: ${(p: TopicPillProps) => p.color ?? theme.gray200};
  color: ${(p: TopicPillProps) => (p.color ? '#fff' : theme.gray800)};
  overflow: visible;
  text-align: left;
  max-height: 100%;
  line-height: 18px;
  box-sizing: border-box;
  cursor: pointer;
  box-shadow: ${TOPIC_PILL_START_SHADOW};
  filter: ${(p: TopicPillProps) => (p.isFocused ? 'brightness(1.1)' : 'none')};
  &:hover {
    filter: brightness(1.1);
  }
  font-weight: ${(p: TopicPillProps) => (p.isFocused ? '500' : 'normal')};
  [data-theme='dark'] & {
    background-color: ${(p: TopicPillProps) => p.color ?? 'rgba(255,255,255,0.1)'};
    color: ${(p: TopicPillProps) => (p.color ? '#fff' : '#e2e8f0')};
  }
`;

type StickyPillLabelProps = { color?: string };

/**
 * Label text that uses position: sticky; left: 0 so when the timeline is scrolled horizontally,
 * the label sticks to the left edge of the scroll container and stays visible even when the
 * pill extends off-screen. Matches pill typography and uses the same background when stuck.
 */
export const StickyPillLabel = styled.span<StickyPillLabelProps>`
  position: sticky;
  left: 0;
  display: inline-block;
  max-width: 100%;
  padding-left: 0px;
  padding-right: 0px;
  color: ${(p: StickyPillLabelProps) => (p.color ? '#fff' : theme.gray800)};
  border-radius: ${TRACK_LABELED_PILL_RADIUS_PX}px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: inherit;
  line-height: inherit;
  z-index: 1;
  [data-theme='dark'] & {
    color: ${(p: StickyPillLabelProps) => (p.color ? '#fff' : '#e2e8f0')};
  }
`;

/**
 * Full-width strip with diagonal hatched pattern for the empty live topics state
 * (no topics detected for the visible range).
 */
export const HatchedStrip = styled.div`
  width: 100%;
  height: 100%;
  background: repeating-linear-gradient(
    45deg,
    ${theme.gray200},
    ${theme.gray200} 2px,
    ${theme.gray100} 2px,
    ${theme.gray100} 4px
  );
  cursor: default;
  [data-theme='dark'] & {
    background: repeating-linear-gradient(
      45deg,
      rgba(255, 255, 255, 0.06),
      rgba(255, 255, 255, 0.06) 2px,
      rgba(255, 255, 255, 0.03) 2px,
      rgba(255, 255, 255, 0.03) 4px
    );
  }
`;
